import React from 'react';
import { Description, Field } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

import BaseModal, { modalSizes } from 'flow/components/ui-components/BaseModal';
import Divider from 'flow/components/ui-components/Divider';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectLobbyChatSessionIsRoom } from 'flow/selectors/meeting-selectors';
import { MeetingAccess } from 'flow/utils/meeting-constants';
import MeetingLobbyJoinInfoContent from './MeetingLobbyJoinInfoContent';
import MeetingAccessDropdown from '../../modals/VideoMeetingRoom/MeetingAccessDropdown';

type Props = {
    meetingCode: string;
    number: string;
    pinCode?: string;
    shouldShowVideo?: boolean;
    allowToggleNewMembers?: boolean;
    meetingAccess: string;
    // May return a promise — awaited before a resulting requireHostPresent
    // change is sent, so the two PUTs on the same conference don't race.
    onChangeMeetingAccess: (meetingAccess: string) => void | Promise<unknown>;
    requireHostPresent: boolean | null;
    onChangeRequireHostPresent: (requireHostPresent: boolean) => void;
    onClose: () => void;
};

// The join-before-host toggle sits below the shared joining-info fields, host-only.
const MeetingLobbyJoiningInfoModal = ({
    meetingCode,
    number,
    pinCode,
    shouldShowVideo,
    allowToggleNewMembers,
    meetingAccess,
    onChangeMeetingAccess,
    requireHostPresent,
    onChangeRequireHostPresent,
    onClose,
}: Props) => {
    const { i18next } = useAppContext();
    const isRoom = useAppSelector(selectLobbyChatSessionIsRoom);
    // A conference that predates meeting-access ever having a value
    // persisted for it (e.g. an older personal conference) reports
    // meetingAccess as null indefinitely, not just while this already-loaded
    // modal's data is in flight — fall back to the historical default rather
    // than showing "Loading" forever.
    const resolvedMeetingAccess =
        meetingAccess ?? MeetingAccess.ANYONE_WITH_LINK.key;
    const noOneCanJoin = resolvedMeetingAccess === MeetingAccess.NO_ONE.key;

    const handleMeetingAccessChange = async (newMeetingAccess: string) => {
        // Chained rather than fired concurrently: both PUTs hit the same
        // conference with a partial DTO, so two in flight at once risk a
        // read-modify-write clobbering one another.
        await onChangeMeetingAccess(newMeetingAccess);
        // Nobody can join on their own, so letting them in before the host
        // does isn't a choice to offer.
        if (newMeetingAccess === MeetingAccess.NO_ONE.key) {
            onChangeRequireHostPresent(true);
        }
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('videoconference.lobby.joining_info')}
            modalSize={modalSizes.SMALL_MEDIUM}
            className="w-[360px] rounded-xl"
            mainClassName="flex flex-col gap-3 py-6"
            disableActions={true}
            onClose={onClose}
        >
            <MeetingLobbyJoinInfoContent
                meetingCode={meetingCode}
                number={number}
                pin={pinCode}
                shouldShowVideo={shouldShowVideo}
            />

            {allowToggleNewMembers && (
                <>
                    <Divider className="my-0" />

                    <div className="flex flex-col gap-3 px-2">
                        <MeetingAccessDropdown
                            value={resolvedMeetingAccess}
                            onChange={handleMeetingAccessChange}
                            hideMembersOnly={!isRoom}
                        />

                        <Divider className="my-0" />

                        <Field
                            as="div"
                            className="flex items-center justify-between gap-3"
                        >
                            <span
                                className={twMerge(
                                    'text-sm text-gray-600',
                                    noOneCanJoin && 'text-gray-400',
                                )}
                            >
                                {i18next.t(
                                    'meetings.create_room.host_presence_toggle_label',
                                )}
                            </span>
                            <div className={noOneCanJoin ? 'opacity-40' : ''}>
                                <Toggle
                                    value={requireHostPresent === false}
                                    loading={requireHostPresent === null}
                                    onChange={(letJoinBeforeHost) =>
                                        onChangeRequireHostPresent(
                                            !letJoinBeforeHost,
                                        )
                                    }
                                    disabled={noOneCanJoin}
                                    text={i18next.t(
                                        'meetings.create_room.host_presence_toggle_label',
                                    )}
                                />
                            </div>
                            {noOneCanJoin ? (
                                // Why the switch is disabled, for screen readers only:
                                // the greyed row already shows it to sighted users.
                                <Description className="sr-only">
                                    {i18next.t(
                                        'meetings.create_room.host_presence_toggle_disabled_reason',
                                    )}
                                </Description>
                            ) : null}
                        </Field>
                    </div>
                </>
            )}
        </BaseModal>
    );
};

export default MeetingLobbyJoiningInfoModal;
